'use client';

import React, { useState } from 'react';
import Button from '@/ui/components/shared/Button';
import { useTranslations } from 'next-intl';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import { FRIENDLY_CAPTCHA_ENDPOINT } from '@/integrations/captcha/constants';
import { useLocalization } from '@/ui/providers/localizationProvider';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldError, useForm } from 'react-hook-form';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import PrivacyPolicyCheckBox from '@/ui/components/tiles/ClubRegistration/components/PrivacyPolicyCheckBox';
import { useSearchParams } from 'next/navigation';
import axios from 'axios';
import ErrorCodeEnum from '@/enums/error-code-enum';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import {
  NukiPremiumReferFriendFormData,
  NukiPremiumReferFriendFormSent,
} from './refer-friend-invite-sent';
import Container from '../../shared/Container';

type NukiPremiumReferAFriendFormProps = {
  trackingPrefix?: string;
};

export function NukiPremiumReferAFriendForm({
  trackingPrefix,
}: Readonly<NukiPremiumReferAFriendFormProps>) {
  const { language } = useLocalization();
  const t = useTranslations('NukiPremiumReferFriendForm');

  const [submissionFailedError, setSubmissionFailedError] = useState('');
  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);
  const [refereeEmails, setRefereeEmails] = useState(['']);
  const searchParams = useSearchParams();

  const formSchema = yup.object({
    referrerFirstName: yup
      .string()
      .required(t('formRequiredReferrerFirstName'))
      .max(50, t('formMaxLength50Chars')),
    referrerLastName: yup
      .string()
      .required(t('formRequiredReferrerLastName'))
      .max(50, t('formMaxLength50Chars')),
    referrerEmail: yup.string().email(t('formInvalidEmail')).required(t('formRequiredEmail')),
    refereeEmails: yup
      .array()
      .of(yup.string().email(t('formInvalidEmail')))
      .test({
        name: 'requiredAtLeastOneRefereeEmail',
        test: (emails) => {
          const passed =
            emails && emails.filter((email) => email && email.trim() !== '').length > 0;

          return passed;
        },
        message: t('formRequiredAtLeastOneRefereeEmail'),
      })
      .max(10, t('formMaxRefereeEmails')),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('formRequiredPrivacyPolicy'))
      .required(t('formRequiredPrivacyPolicy')),
    language: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<yup.InferType<typeof formSchema>>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      referrerFirstName: searchParams.get('referrerFirstName') || '',
      referrerLastName: searchParams.get('referrerLastName') || '',
      referrerEmail: searchParams.get('referrerEmail') || '',
      refereeEmails,
      privacyPolicy: false,
      language,
    },
  });

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'auto',
    language: language as any,
    showAttribution: true,
  });

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    setSubmissionFailedError('');

    try {
      const parsedData = {
        referrerFirstName: data.referrerFirstName,
        referrerLastName: data.referrerLastName,
        referrerEmail: data.referrerEmail,
        refereeEmails: data.refereeEmails?.filter((email) => !!email),
        privacyPolicy: data.privacyPolicy,
        frcCaptchaSolution: captchaHook.captchaStatus.solution as string,
        language,
      };

      await axios.post('/api/forms/refer-a-friend/nuki-premium', parsedData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setIsSubmissionSuccess(true);
      DataLayerHelper.pushFormSubmit({ formId: 'nuki-premium-refer-friend' });
    } catch (error: any) {
      const responseData = await error?.response?.data;
      const errorCode = responseData?.errorCode;

      switch (errorCode) {
        case ErrorCodeEnum.CAPTCHA_FAILED:
          setSubmissionFailedError(t('formErrorCaptchaValidationFailed'));
          break;
        case ErrorCodeEnum.NUKI_PREMIUM_LIFETIME_REFERRER_MUST_BE_A_NUKI_PREMIUM_USER:
        case ErrorCodeEnum.NUKI_PREMIUM_LIFETIME_REFERRER_NOT_FOUND:
          setSubmissionFailedError(t('formErrorReferrerMustBeNukiPremiumUser'));
          break;
        default:
          setSubmissionFailedError(t('formErrorSubmissionFailed'));
          break;
      }

      captchaHook.resetWidget();
    }
  };

  if (isSubmissionSuccess) {
    return (
      <Container>
        <NukiPremiumReferFriendFormSent
          formData={getValues() as NukiPremiumReferFriendFormData}
          trackingPrefix={trackingPrefix}
        />
      </Container>
    );
  }

  return (
    <Container>
      <div className="mb-4 flex justify-center">
        <form
          className="grid max-w-[56rem] grid-cols-1 gap-y-6"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <div className="mb-2 sm:mb-8">
            <h1 className="mb-3 text-center text-[1.625em] font-bold sm:text-[3em]">
              {t('formTitle')}
            </h1>
            <h2 className="text-center text-md leading-6 sm:text-2xl">{t('formSubtitle')}</h2>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 md:gap-8">
            <div>
              <TextInput
                id="referrerFirstName"
                register={register}
                errors={errors.referrerFirstName}
                label={t('formLabelReferrerFirstName')}
                placeholder={t('formLabelReferrerFirstName')}
                required
              />
            </div>
            <div>
              <TextInput
                id="referrerLastName"
                register={register}
                errors={errors.referrerLastName}
                label={t('formLabelReferrerLastName')}
                placeholder={t('formLabelReferrerLastName')}
                required
              />
            </div>
          </div>

          <div>
            <div>
              <TextInput
                id="referrerEmail"
                register={register}
                errors={errors.referrerEmail}
                label={t('formLabelReferrerEmail')}
                placeholder={t('formLabelReferrerEmail')}
                required
                type="email"
              />
            </div>
          </div>

          <div className="space-y-4">
            {refereeEmails.map((email, index) => {
              let error = errors.refereeEmails?.[index];

              if (!error && index === 0) {
                error = errors.refereeEmails as FieldError;
              }

              return (
                <div key={index} className="relative">
                  <TextInput
                    id={`refereeEmails${index}`}
                    name={`refereeEmails[${index}]`}
                    register={register}
                    errors={error}
                    label={index === 0 ? t('formLabelRefereeEmail') : ''}
                    placeholder={t('formLabelRefereeEmail')}
                    required
                    type="email"
                  />
                </div>
              );
            })}
          </div>

          <div>
            <Button
              className="py-2"
              variant="black"
              type="button"
              disabled={refereeEmails.length >= 5}
              onClick={() => {
                setRefereeEmails([...refereeEmails, '']);
              }}
              data-tile-element-id={buildTrackingId(trackingPrefix, 'add-referee')}
            >
              {t('buttonAddReferee')}
            </Button>
          </div>

          <div className="space-y-4">
            <PrivacyPolicyCheckBox register={register} errors={errors.privacyPolicy} />
          </div>

          <div>
            <div className="inline-block min-w-min">
              {captchaHook.CaptchaWidget({ className: 'relative' })}
            </div>
          </div>

          <div>{submissionFailedError && <p className="text-red">{submissionFailedError}</p>}</div>

          <div>
            <Button
              type="submit"
              className="py-2"
              disabled={isSubmitting}
              loading={isSubmitting}
              data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
            >
              {t('formSubmitBtn')}
            </Button>
          </div>
        </form>
      </div>
    </Container>
  );
}
